import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import React, { useEffect, useRef } from "react";
import { COLORS } from "utils/colors";

/**
 * PieChart React component renders a donut pie chart using D3.
 * @memberof module:components/graphs
 * @param {Object} props - Component props
 * @param {number} props.width - Width of the SVG element
 * @param {number} props.height - Height of the SVG element
 * @param {Object.<string, {value: number, color: string}>} props.dataset - Data to visualize, keys as categories with value and color
 *
 * Example dataset format:
 * {
 *   category1: { value: 10, color: "#ff0000" },
 *   category2: { value: 20, color: "#00ff00" },
 *   ...
 * }
 *
 * @returns {JSX.Element} The rendered pie chart component
 */
const PieChart = ({ width, height, dataset }) => {
  const ref = useRef();

  // On component mount, clear SVG and draw with animation
  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();
    draw(true);
  }, []);

  // On dataset change, clear SVG and redraw without animation
  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();
    draw(false);
  }, [dataset]);

  /**
   * Draws the donut pie chart in the SVG container.
   *
   * @param {boolean} animationEnabled - Whether to animate the chart drawing
   */
  const draw = (animationEnabled) => {
    // Create dummy data
    var margin = 40;

    var radius = Math.min(width, height) / 2 - margin;

    var data = dataset;

    // set the color scale
    var color = d3
      .scaleOrdinal()
      .domain(Object.keys(dataset))
      .range(Object.values(dataset).map((item) => item.color));

    // Compute the position of each group on the pie:
    var pie = d3
      .pie()
      .sort(null) // Do not sort group by size
      .value(function (d) {
        return d.value;
      })
      .padAngle(0.03);

    var data_ready = pie(
      Object.entries(data)
        .filter(([key, val]) => val.value !== 0)
        .map((el) => {
          return { key: el[0], color: el[1].color, value: el[1].value };
        }),
    );

    // The arc generator
    var arc = d3
      .arc()
      .innerRadius(radius * 0.5) // This is the size of the donut hole
      .outerRadius(radius * 0.8)
      .cornerRadius(5);

    // Another arc that won't be drawn. Just for labels positioning
    var outerArc = d3
      .arc()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    var svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

    if (
      data.Down.value === 0 &&
      data.Running.value === 0 &&
      data["Warning"].value === 0
    )
      data_ready = [
        {
          data: {
            key: "No available units",
            color: "#5a5a5a",
            value: 0,
          },
          index: 0,
          value: 0,
          startAngle: 0,
          endAngle: 360,
          padAngle: 0.03,
        },
      ];

    svg
      .selectAll("allSlices")
      .data(data_ready)
      .enter()
      .append("path")
      .attr("id", function (d, i) {
        return "slice" + i;
      })
      .attr("d", arc)
      .attr("fill", function (d) {
        return color(d.data.key) + "80";
      })
      .style("stroke", function (d) {
        return color(d.data.key);
      })
      .style("stroke-width", "2px");

    svg
      .selectAll("maskSlice")
      .data(data_ready)
      .enter()
      .append("path")
      .attr("id", function (d, i) {
        return "maskSlice" + i;
      })
      .attr("d", arc)
      .style("opacity", 0)
      .attr("fill", "black")
      .on("mouseover", function (i, d) {
        var posA = arc.centroid(d);

        d3.select("#line" + d.index)
          .attr("points", function (d) {
            var posA = arc.centroid(d); // line insertion in the slice
            var posB = outerArc.centroid(d); // line break: we use the other arc generator that has been built only for that
            var posC = outerArc.centroid(d); // Label position = almost the same as posB
            var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2; // we need the angle to see if the X position will be at the extreme right or extreme left
            posC[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
            posA[0] += midangle < Math.PI ? 10 : -10;
            posB[0] += midangle < Math.PI ? 10 : -10;
            posC[0] += midangle < Math.PI ? 10 : -10;
            return [posA, posB, posC];
          })
          .style("stroke", COLORS.selectedColorTheme)
          .style("stroke-width", "3px");

        const label = d3.select("#label" + d.index);

        label
          .attr("fill", COLORS.selectedColorTheme)
          .attr("text-anchor", "middle") // 👈 IMPORTANT
          .attr("transform", function (d) {
            if (d.data.key === "No available units") return "";

            var pos = outerArc.centroid(d);
            var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;

            pos[0] =
              radius * 0.99 * (midangle < Math.PI ? 1 : -1) +
              (midangle < Math.PI ? 10 : -10);

            return "translate(" + pos + ")";
          })
          .style("stroke", COLORS.selectedColorTheme)
          .style("stroke-width", "0.7px");

        // reset old content
        label.selectAll("*").remove();

        // LINE 1 (key)
        label.append("tspan").attr("x", 0).attr("dy", "0em").text(d.data.key);

        const total = d3.sum(Object.values(dataset), (d) => d.value);
        const percent = ((d.data.value / total) * 100).toFixed(1);
        // LINE 2 (value)
        label
          .append("tspan")
          .attr("x", 0)
          .attr("dy", "1.2em")
          .text(`${percent}%`);

        d3.select("#slice" + d.index)
          .attr(
            "transform",
            "translate(" +
              Math.sign(posA[0]) * 5 +
              "," +
              Math.sign(posA[1]) * 5 +
              ")",
          )
          .style("stroke", COLORS.selectedColorTheme)
          .attr("transform", "scale(1.2)");
      })
      .on("mouseout", function (i, d) {
        d3.select("#line" + d.index)
          .attr("points", function (d) {
            var posA = arc.centroid(d); // line insertion in the slice
            var posB = outerArc.centroid(d); // line break: we use the other arc generator that has been built only for that
            var posC = outerArc.centroid(d); // Label position = almost the same as posB
            var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2; // we need the angle to see if the X position will be at the extreme right or extreme left
            posC[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
            return [posA, posB, posC];
          })
          .style("stroke", "#fff")
          .style("stroke-width", "3px");

        d3.select("#label" + d.index)
          .attr("fill", "white")
          .attr("transform", function (d) {
            if (d.data.key === "No available units") return "";
            var pos = outerArc.centroid(d);
            var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
            pos[0] = radius * 0.99 * (midangle < Math.PI ? 1 : -1);
            return "translate(" + pos + ")";
          })
          .style("stroke-width", "0px");

        d3.select("#slice" + d.index)
          .attr("transform", "translate(0,0)")
          .style("stroke", data_ready[d.index].data.color);
      });

    svg
      .selectAll("allPolylines")
      .data(data_ready)
      .enter()
      .append("polyline")
      .attr("stroke", "white")
      .attr("opacity", function (d) {
        if (d.data.value === 0) return 0;
        else return 1;
      })
      .style("fill", "none")
      .attr("stroke-width", 3)
      .attr("id", function (d, i) {
        return "line" + i;
      })
      .attr("points", function (d) {
        var posA = arc.centroid(d); // line insertion in the slice
        var posB = outerArc.centroid(d); // line break: we use the other arc generator that has been built only for that
        var posC = outerArc.centroid(d); // Label position = almost the same as posB
        var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2; // we need the angle to see if the X position will be at the extreme right or extreme left
        posC[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
        return [posA, posB, posC];
      })
      .attr("stroke-dasharray", function (d) {
        const polyline = this;
        const totalLength = polyline.getTotalLength();
        return totalLength + " " + totalLength;
      })
      .attr("stroke-dashoffset", function (d) {
        const polyline = this;
        const totalLength = polyline.getTotalLength();
        return totalLength;
      })
      .transition() // Add the transition
      .duration(animationEnabled ? 2000 : 0) // Transition duration in milliseconds
      .attr("stroke-dashoffset", 0); // Set the final stroke-dashoffset value

    svg
      .selectAll("allLabels")
      .data(data_ready)
      .enter()
      .append("text")
      .attr("id", function (d, i) {
        return "label" + i;
      })
      .text(function (d) {
        return d.data.key;
      })
      .attr("fill", function (d) {
        if (d.data.key === "No available units") return "white";
        if (d.data.value === 0) return "transparent";
        else return "white";
      })
      .attr("font-weight", 600)
      .attr("opacity", 1)
      .attr("transform", function (d) {
        var pos = outerArc.centroid(d);

        if (d.data.key === "No available units") return "";

        var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = radius * 0.99 * (midangle < Math.PI ? 1 : -1);
        return "translate(" + pos + ")";
      })
      .style("text-anchor", function (d) {
        if (d.data.key === "No available units") return "middle";

        var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midangle < Math.PI ? "start" : "end";
      })
      .transition() // Add the transition
      .duration(animationEnabled ? 2000 : 0) // Transition duration in milliseconds
      .attr("opacity", 1);

    svg
      .selectAll("path")
      .transition()
      .duration(animationEnabled ? 2000 : 0) // Transition duration in milliseconds
      .attrTween("d", function (d) {
        var interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
        return function (t) {
          return arc(interpolate(t));
        };
      });
  };
  return (
    <>
      <div>
        <svg ref={ref}></svg>
      </div>
    </>
  );
};

export default PieChart;
